import {useQuery} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';
import {Helmet} from 'react-helmet';
import {NumberParam, useQueryParam} from 'use-query-params';

import ErrorPage from 'app/components/Error/ErrorPage';
import Loading from 'app/components/Loading';
import StoryView from 'app/components/StoryView';
import {useStorySlides} from 'app/components/StoryView/storySlides';
import {api} from 'app/modules/Remote';
import {ApiPublicLensResponse, ApiPublicNote, ApiPublicReportMeta} from 'app/modules/Remote/api';
import {ApiFeature, ApiNote, ApiReport, ReportState} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {DEFAULT_COLORS} from 'app/pages/MonitorProjectView/SwatchColorPicker';
import {PublicFeatureDataProvider} from 'app/providers/FeatureDataProvider';
import {GeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import {StateApiNote, hydrateApiNote} from 'app/stores/NotesStore';
import {recordEvent, usePublicViewerAnalytics} from 'app/tools/Analytics';
import {HideHubspotWidget} from 'app/tools/Hubspot';
import * as layerUtils from 'app/utils/layerUtils';
import {getMultiFeaturePropertyParts} from 'app/utils/multiFeaturePropertyUtils';
import {makeShareLinkOverlaySettings} from 'app/utils/overlayUtils';
import * as routeUtils from 'app/utils/routeUtils';

/**
 * Overlays render from their feature collection's PMTiles, so a story never loads overlay
 * geometry. useGeoJsonFeaturesLoader's contract still has to be met for the map.
 */
const NO_GEOJSON_OVERLAYS: GeoJsonFeaturesLoader = () =>
  I.List() as unknown as I.ImmutableOf<ApiFeature[]>;

const EMPTY_NOTES: StateApiNote[] = [];
// Every overlay starts hidden; a slide turns on the ones its note or page chose.
const NO_OVERLAYS_VISIBLE: Record<string, boolean> = {};

/**
 * The `/p/<uuid>` payload of a link that points at a report and was made for the story page.
 */
export type StorySharePayload = ApiPublicLensResponse & {report: ApiPublicReportMeta};

interface Props {
  uuid: string;
  payload: StorySharePayload;
  isEmbedded: boolean;
}

/**
 * A saved report, presented as a map slideshow for someone with no Lens account.
 *
 * Loads through the share link's own routes, which serve the same shapes the signed-in story
 * page reads, minus imagery from sources we can't redistribute. Notes on that imagery have
 * nothing to show, so the slides leave them out and say how many.
 */
const PublicStory: React.FunctionComponent<Props> = ({uuid, payload, isEmbedded}) => {
  usePublicViewerAnalytics();

  const reportMeta = payload.report;

  const organization = React.useMemo(
    () => I.fromJS(payload.organization) as I.ImmutableOf<ApiOrganization>,
    [payload.organization]
  );
  const features = React.useMemo(
    () => I.fromJS(payload.features) as I.ImmutableListOf<ApiFeature>,
    [payload.features]
  );
  const featureCollection = React.useMemo(
    () => I.fromJS(payload.feature_collection) as I.ImmutableOf<ApiFeatureCollection>,
    [payload.feature_collection]
  );
  const overlayFeatureCollections = React.useMemo(
    () => I.fromJS(payload.overlay_feature_collections) as I.ImmutableOf<ApiFeatureCollection[]>,
    [payload.overlay_feature_collections]
  );
  const overlaySettings = React.useMemo(
    () =>
      makeShareLinkOverlaySettings(
        payload.overlay_feature_collections,
        payload.overlay_configs ?? [],
        DEFAULT_COLORS
      ),
    [payload.overlay_feature_collections, payload.overlay_configs]
  );

  const anchorFeature = React.useMemo(
    () => features.find((f) => f!.getIn(['properties', 'id']) === reportMeta.featureId) ?? null,
    [features, reportMeta.featureId]
  );

  // Every part of the property the link serves. Overview pages index into this list, and
  // it's what the map outlines.
  const propertyParts = React.useMemo<I.ImmutableOf<ApiFeature[]>>(
    () =>
      anchorFeature
        ? I.List(getMultiFeaturePropertyParts(features, anchorFeature).map((p) => p.feature))
        : I.List(),
    [features, anchorFeature]
  );

  // A report only carries notes from other parts when it was saved as a multi-location report.
  const noteFeatureIds = React.useMemo(
    () =>
      reportMeta.isMultilocation
        ? propertyParts.map((f) => f!.get('id') as number).toArray()
        : [reportMeta.featureId],
    [reportMeta.isMultilocation, reportMeta.featureId, propertyParts]
  );

  const reportQuery = useQuery({
    queryKey: ['public-story-report', uuid],
    queryFn: async () => {
      const response = await api.public.report(uuid);
      return response.get('data').toJS() as ApiReport<ReportState>;
    },
  });
  const report = reportQuery.data ?? null;

  const notesQuery = useQuery({
    queryKey: ['public-story-notes', uuid, noteFeatureIds],
    queryFn: async () => {
      const responses = await Promise.all(
        noteFeatureIds.map((featureId) => api.public.featureNotes(uuid, featureId))
      );
      return responses
        .flatMap((response) => response.get('data').toJS() as ApiPublicNote[])
        .map(hydratePublicNote);
    },
    enabled: anchorFeature !== null,
  });
  const notes = notesQuery.data ?? EMPTY_NOTES;

  const enrolledLayerKeys = React.useMemo(
    () => layerUtils.getEnrolledLayerKeysForFeatureCollection(featureCollection),
    [featureCollection]
  );
  const {slides, skippedCount, unshareableCount} = useStorySlides({
    report,
    notes,
    anchorFeature,
    propertyParts,
    organizationName: organization.get('name'),
    enrolledLayerKeys,
    omitUnshareable: true,
  });

  // Once per story opened, after its slides are built.
  const isStoryLoaded = report !== null && notesQuery.isSuccess;
  const organizationId = organization.get('id');
  const slideCount = slides.length;
  React.useEffect(() => {
    if (isStoryLoaded) {
      recordEvent('Viewed public story', {
        reportId: reportMeta.id,
        organizationId,
        isEmbedded,
        slideCount,
        unshareableCount,
      });
    }
  }, [isStoryLoaded, reportMeta.id, organizationId, isEmbedded, slideCount, unshareableCount]);

  const [slideParam, setSlideParam] = useQueryParam(
    routeUtils.STORY_SLIDE_QUERY_PARAM,
    NumberParam
  );
  const requestedSlide = Number.isInteger(slideParam) ? (slideParam as number) : 1;
  const slideIndex = Math.min(Math.max(requestedSlide - 1, 0), Math.max(slides.length - 1, 0));
  const onChangeSlide = React.useCallback(
    (index: number) => setSlideParam(index + 1, 'replaceIn'),
    [setSlideParam]
  );

  if (!anchorFeature || reportQuery.isError || notesQuery.isError) {
    return (
      <ErrorPage
        message="This content is no longer available."
        linkTo={isEmbedded ? null : 'https://www.upstream.tech/lens'}
      />
    );
  }

  if (!report || notesQuery.isPending) {
    return <Loading />;
  }

  return (
    <PublicFeatureDataProvider uuid={uuid}>
      <Helmet>
        <title>{reportMeta.title} | Lens</title>
      </Helmet>
      <HideHubspotWidget />
      <StoryView
        title={reportMeta.title}
        backUrl={null}
        slides={slides}
        skippedCount={skippedCount}
        unshareableCount={unshareableCount}
        isEmbedded={isEmbedded}
        slideIndex={slideIndex}
        onChangeSlide={onChangeSlide}
        isMultilocation={reportMeta.isMultilocation}
        features={propertyParts}
        featureCollection={featureCollection}
        overlayFeatureCollections={overlayFeatureCollections}
        overlaySettings={overlaySettings}
        defaultOverlayVisibility={NO_OVERLAYS_VISIBLE}
        geoJsonFeaturesLoader={NO_GEOJSON_OVERLAYS}
        organization={organization}
        firebaseToken=""
      />
    </PublicFeatureDataProvider>
  );
};

/**
 * The public note is narrower than the signed-in one (no author id, org or project), and the
 * story reads none of that. The byline comes on the note itself.
 */
function hydratePublicNote(note: ApiPublicNote): StateApiNote {
  return hydrateApiNote(note as ApiNote, {name: note.userName ?? null, isAuthor: false});
}

export default PublicStory;
